import { Avatar, Grid } from '@material-ui/core';
import ExitToApp from '@material-ui/icons/ExitToApp';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import Settings from '@material-ui/icons/Settings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { showCashierPreferencesView } from 'src/preferences/business-logic/handle-preferences-actions';
import SizedGridItem from 'src/sized-grid-item/sized-grid-item';
import { DeviceSettings, Inventory, Shifts } from '../../media';
import MenuFooter from '../menu-footer';
import HomeMenuPanelItem from '../home-menu-panel-item';
import { styles } from './styles';
import { AppState } from 'src/common/redux/configure-store';
import { HomeMenuPanelItemProps } from '../home-menu-panel-item/home-menu-panel-item';
import NTEPCertification from 'src/home-page/components/home-page-layout/ntep-certification';
import { IPosUiMenu } from 'src/common/interfaces/states/IPosUiMenuState';
import { connect } from 'react-redux';
import XLRLogger from 'src/common/helpers/logger';

interface HomeMenuProps extends IHomeMenuState {
  trainingModeActive: boolean;
  displayName?: string;
  touchPointId?: string;
  time: string;
  toggleMenuDisplay: () => void;
  handleLogout: () => void;
  isActiveTransaction: boolean;
  toggleTrainingMode: () => void;
}

const HomeMenu = ({
  trainingModeActive,
  displayName,
  touchPointId,
  time,
  toggleMenuDisplay,
  handleLogout,
  isActiveTransaction,
  userCommandMenuConfig,
}: HomeMenuProps): JSX.Element => {
  const classes = styles();
  const trainingModeClasses = trainingModeStyles();
  const { t } = useTranslation();

  XLRLogger.info({
    message: `home-menu using menu-id: ${userCommandMenuConfig.menuType}`,
    source: 'home-menu.ts',
  });
  const homeMenuCommands = userCommandMenuConfig.commands;

  /** Note to devs and maintainers: these maps are a temporary solution https://github.com/ncr-swt-retail/retail-desktop-pos/pull/2594#discussion_r660808083 */
  const icons = {
    'device-settings': <DeviceSettings />,
    settings: <Settings />,
    shifts: <Shifts />,
    'keyboard-arrow-down': <KeyboardArrowDown />,
    inventory: <Inventory />,
  };

  /** Note to devs and maintainers: these maps are a temporary solution https://github.com/ncr-swt-retail/retail-desktop-pos/pull/2594#discussion_r660808083 */
  const actions: { [key: string]: () => void } = {
    ShowCashierPreferencesCommand: async () => {
      await showCashierPreferencesView();
      toggleMenuDisplay();
    },
  };

  const configurationMenuItemsProps: HomeMenuPanelItemProps[] = homeMenuCommands.map(btnConf => {
    const item: HomeMenuPanelItemProps = {
      key: btnConf.commandId,
      button: {
        'data-testid': `${btnConf.commandId}-button`,
        startIcon: btnConf.commandIcon && icons[btnConf.commandIcon],
        className: `${classes.configurationMenuButton} fullWidth`,
        onClick: btnConf.commandId ? actions[btnConf.commandId] : () => {},
        classes: {
          startIcon: btnConf.commandIcon && classes.homeMenuIcon,
        },
      },
      label: {
        className: `${classes.configurationMenuLabel} fullWidth`,
        code: t(btnConf.commandId),
      },
    };
    return item;
  });

  return (
    <>
      <SizedGridItem size={'6.51vh'}>
        <Grid
          container
          className={`${classes.informationContainer} ${
            trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
          } fullWidth fullHeight`}
          justifyContent='center'
          alignContent='center'
        >
          <NTEPCertification className={classes.posInformation} />
        </Grid>
      </SizedGridItem>
      <SizedGridItem size={'16.14vh'}>
        <Grid
          container
          className={`${classes.loggedInUserContainer} ${
            trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
          } fullWidth fullHeight`}
        >
          <Grid item xs={2}>
            {/* What format will user profile images be? */}
            <Avatar className={classes.userImage} data-testid='avatar-label'>
              {displayName ? displayName.substr(0, 1) : 'U'}
            </Avatar>
          </Grid>
          <Grid item xs={10}>
            <Grid container className={`${classes.userInformationContainer} fullWidth fullHeight`}>
              <Grid item xs={12} className={`userName`}>
                {displayName}
              </Grid>
              <Grid item xs={12} className={`unitNumber`} data-testid='touchpointIdLabel'>
                {touchPointId ? `POS ${touchPointId}` : ''}
              </Grid>
              <Grid item xs={12} className={`timeStamp`}>
                {time}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SizedGridItem>
      <Grid
        item
        className={`flexFull fullHeight  ${
          trainingModeActive ? trainingModeClasses.trainingModeBackground : ''
        }`}
      >
        <Grid
          container
          className={`${classes.configurationMenuContainer} fullWidth fullHeight`}
          alignContent='flex-start'
        >
          {configurationMenuItemsProps.map(HomeMenuPanelItem)}
        </Grid>
      </Grid>
      <MenuFooter
        trainingModeActive={trainingModeActive}
        buttons={[
          {
            label: t('Logout'),
            'data-testid': 'logout-user',
            className: 'logOutButton',
            onClick: () => {
              handleLogout();
              toggleMenuDisplay();
            },
            startIcon: <ExitToApp />,
            buttonType: 'white',
            color: 'default',
            disabled: isActiveTransaction,
          },
          {
            label: t('HidePanel'),
            'data-testid': 'hide-menu-panel',
            className: 'hideButton',
            onClick: toggleMenuDisplay,
          },
        ]}
      />
    </>
  );
};

interface IHomeMenuState {
  userCommandMenuConfig: IPosUiMenu;
}

const mapStateToProps = (state: AppState): IHomeMenuState => ({
  userCommandMenuConfig: state.posUiMenu.User,
});

export default connect(mapStateToProps)(HomeMenu);
